import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Checkbox,
  Card,
  CardContent,
  InputAdornment,
  FormControlLabel,
  Switch,
  Divider,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Tag as TagIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  SelectAll as SelectAllIcon,
  Inventory as InventoryIcon,
  AutoAwesome as AutoAwesomeIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import api from '../services/api';

// Type definitions
interface Product {
  id: number;
  title: string;
  sku?: string;
  price: string | number;
  inventory: number;
  tags: string[];
  image?: string;
  variants_count?: number;
  created_at?: string;
  createdAt?: string;
}

interface ApiResponse {
  products: Product[];
  pagination?: {
    limit: number;
    total_products: number;
    has_more: boolean;
    last_product_id?: number;
    since_id?: number;
  };
  all_tags?: string[];
}

type MessageType = 'success' | 'error' | 'warning' | 'info';

// Type definitions for Tag Suggestion Engine
interface ClothingTypeConfig {
  keywords: string[];
  priority: number;
}

interface SpecialPattern {
  pattern: RegExp;
  tags: string[];
}

interface TagMatch {
  tag: string;
  priority: number;
  strength: number;
}

// Integrated Tag Suggestion Engine
class ClothingTagSuggestionEngine {
  private clothingTypes: { [key: string]: ClothingTypeConfig };
  private specialPatterns: SpecialPattern[];

  constructor() {
    this.clothingTypes = {
      '上衣': {
        keywords: ['上衣', '衣', '衫'],
        priority: 2
      },
      'T恤': {
        keywords: ['T恤', 'T-shirt', 'tee', '短袖T'],
        priority: 9
      },
      '短袖': {
        keywords: ['短袖'],
        priority: 8
      },
      '襯衫': {
        keywords: ['襯衫', '襯衣', '恤衫'],
        priority: 9
      },
      '針織衣': {
        keywords: ['針織', '毛衣', '毛線', '針織衫'],
        priority: 8
      },
      '大學T': {
        keywords: ['大學T', '衛衣', '圓領', '學院'],
        priority: 9
      },
      '帽T': {
        keywords: ['帽T', '連帽', '帽衫'],
        priority: 9
      },
      '針織外套': {
        keywords: ['針織外套', '毛衣外套', '開襟針織', '針織開衫'],
        priority: 9
      },
      '西裝外套': {
        keywords: ['西裝外套', '西裝', '西服', '正裝外套', '外套西裝'],
        priority: 9
      },
      '外套': {
        keywords: ['外套', '夾克', '風衣', '大衣'],
        priority: 7
      },
      '坦克': {
        keywords: ['坦克', '背心', '無袖'],
        priority: 9
      },
      '褲子': {
        keywords: ['褲子', '長褲', '褲'],
        priority: 3
      },
      '短褲': {
        keywords: ['短褲', '五分褲', '七分褲'],
        priority: 9
      },
      '西裝褲': {
        keywords: ['西裝褲', '西褲', '正裝褲', '西服褲', '落地褲', '商務褲', '寬鬆落地褲'],
        priority: 9
      },
      '棉褲': {
        keywords: ['棉褲', '休閒褲', '運動褲', '棉質褲'],
        priority: 8
      },
      '卡其褲': {
        keywords: ['卡其褲', '卡其', '工作褲'],
        priority: 9
      },
      '牛仔褲': {
        keywords: ['牛仔褲', '牛仔', '丹寧褲', '直筒牛仔'],
        priority: 9
      },
      '皮帶': {
        keywords: ['皮帶', '腰帶', '皮製腰帶', '編織皮帶'],
        priority: 10
      },
      '項鍊': {
        keywords: ['項鍊', '頸鍊', '吊墜'],
        priority: 10
      },
      '手鍊': {
        keywords: ['手鍊', '手環', '手飾'],
        priority: 10
      },
      '領帶': {
        keywords: ['領帶', '領結', '領巾'],
        priority: 10
      },
      '包包': {
        keywords: ['包包', '手提包', '背包', '側背包', '肩背包', '公事包', '托特包', '實用洗士錢包'],
        priority: 9
      }
    };

    this.specialPatterns = [
      {
        pattern: /針織.*V領/,
        tags: ['針織衣', '上衣']
      },
      {
        pattern: /西裝.*落地褲/,
        tags: ['西裝褲', '褲子']
      },
      {
        pattern: /涼感.*短袖/,
        tags: ['短袖', '上衣']
      },
      {
        pattern: /polo.*衫/,
        tags: ['襯衫', '上衣']
      },
      {
        pattern: /半高.*針織/,
        tags: ['針織外套', '外套', '上衣']
      },
      {
        pattern: /寬鬆.*落地褲/,
        tags: ['西裝褲', '褲子']
      },
      {
        pattern: /直筒.*牛仔/,
        tags: ['牛仔褲', '褲子']
      },
      {
        pattern: /muscle.*fit/,
        tags: ['短袖', '上衣']
      }
    ];
  }

  suggestTags(productTitle: string, existingTags: string[] = [], createdDate: string | null = null): string[] {
    const suggestions = new Set<string>();
    const title = productTitle.toLowerCase();
    
    this.detectClothingTypes(title, suggestions);
    this.applySpecialPatterns(title, suggestions);
    this.detectNewArrival(title, suggestions, createdDate);
    
    const filteredSuggestions = Array.from(suggestions).filter(tag => 
      !existingTags.includes(tag)
    );
    
    return this.sortByPriority(filteredSuggestions);
  }

  private detectClothingTypes(title: string, suggestions: Set<string>): void {
    const matches: TagMatch[] = [];
    
    Object.entries(this.clothingTypes).forEach(([tag, config]) => {
      const hasMatch = config.keywords.some(keyword => 
        title.includes(keyword)
      );
      
      if (hasMatch) {
        matches.push({
          tag,
          priority: config.priority,
          strength: Math.max(...config.keywords
            .filter(keyword => title.includes(keyword))
            .map(keyword => keyword.length)
          )
        });
      }
    });

    this.resolveTagConflicts(matches, suggestions);
  }

  private applySpecialPatterns(title: string, suggestions: Set<string>): void {
    this.specialPatterns.forEach(pattern => {
      if (pattern.pattern.test(title)) {
        pattern.tags.forEach(tag => suggestions.add(tag));
      }
    });
  }

  private resolveTagConflicts(matches: TagMatch[], suggestions: Set<string>): void {
    matches.sort((a, b) => {
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      return b.strength - a.strength;
    });

    matches.forEach(match => {
      const { tag } = match;
      
      if (tag === '西裝褲') {
        suggestions.add(tag);
        suggestions.add('褲子');
      } else if (tag === '短褲' || tag === '棉褲' || tag === '卡其褲' || tag === '牛仔褲') {
        suggestions.add(tag);
        suggestions.add('褲子');
      } else if (tag === 'T恤' || tag === '短袖') {
        suggestions.add(tag);
        suggestions.add('上衣');
      } else if (tag === '襯衫' || tag === '針織衣' || tag === '大學T' || tag === '帽T' || tag === '坦克') {
        suggestions.add(tag);
        suggestions.add('上衣');
      } else if (tag === '針織外套' || tag === '西裝外套') {
        suggestions.add(tag);
        suggestions.add('外套');
        suggestions.add('上衣');
      } else if (tag === '外套') {
        suggestions.add(tag);
        suggestions.add('上衣');
      } else {
        suggestions.add(tag);
      }
    });
  }

  private detectNewArrival(title: string, suggestions: Set<string>, createdDate: string | null): void {
    const newKeywords = ['新', '新品', '新款', '最新', '新到', '新上市'];
    const hasNewInTitle = newKeywords.some(keyword => title.includes(keyword));
    
    let isRecentlyListed = false;
    if (createdDate) {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      
      const productDate = new Date(createdDate);
      isRecentlyListed = productDate > oneMonthAgo;
    }
    
    if (hasNewInTitle || isRecentlyListed) {
      suggestions.add('New');
    }
  }

  private sortByPriority(suggestions: string[]): string[] {
    const priorityOrder = [
      'New',
      'T恤', '短袖', '襯衫', '針織衣', '大學T', '帽T', '坦克',
      '針織外套', '西裝外套', 
      '短褲', '西裝褲', '牛仔褲', '卡其褲', '棉褲',
      '皮帶', '項鍊', '手鍊', '領帶', '包包',
      '外套', '上衣', '褲子'
    ];

    return suggestions.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a);
      const bIndex = priorityOrder.indexOf(b);
      
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
  }
}

const ProductTagManager: React.FC = () => {
  // State for products and filtering
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [lastProductId, setLastProductId] = useState<number | null>(null);
  
  // State for selection and bulk operations
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [bulkTag, setBulkTag] = useState<string>('');
  const [bulkOperationLoading, setBulkOperationLoading] = useState<boolean>(false);
  
  // State for available tags
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loadingTags, setLoadingTags] = useState<boolean>(false);
  
  // State for editing
  const [editingProduct, setEditingProduct] = useState<number | null>(null);
  const [editTags, setEditTags] = useState<string>('');
  const [editLoading, setEditLoading] = useState<boolean>(false);
  
  // State for UI
  const [message, setMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [messageType, setMessageType] = useState<MessageType>('success');
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // State for tag suggestions
  const [showSuggestions, setShowSuggestions] = useState<{[key: number]: string[]}>({});
  const [autoSuggestLoading, setAutoSuggestLoading] = useState<boolean>(false);
  const [tagEngine] = useState(() => new ClothingTagSuggestionEngine());

  // Show message function
  const showMessage = (msg: string, type: MessageType = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Fetch products from API
  const fetchProducts = useCallback(async (sinceId: number | null = null, append: boolean = false) => {
    try {
      if (!append) setLoading(true);
      
      const params = new URLSearchParams({
        limit: '50',
        search: searchTerm,
        tag_filter: tagFilter
      });

      if (sinceId) {
        params.set('since_id', sinceId.toString());
      }

      console.log('Fetching products with params:', params.toString());

      const response = await api.get<ApiResponse>(`/api/shopify/products/manage-tags?${params}`);
      
      if (response.data) {
        const newProducts = response.data.products || [];
        
        if (append) {
          setProducts((prev: Product[]) => [...prev, ...newProducts]);
        } else {
          setProducts(newProducts);
        }
        
        setHasMore(response.data.pagination?.has_more || false);
        setLastProductId(response.data.pagination?.last_product_id || null);
        
        if (response.data.all_tags) {
          setAllTags(response.data.all_tags);
        }

        console.log(`Loaded ${newProducts.length} products. Total: ${append ? products.length + newProducts.length : newProducts.length}. Has more: ${response.data.pagination?.has_more}`);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      showMessage('Failed to fetch products', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, tagFilter]);

  // Fetch all available tags
  const fetchAllTags = useCallback(async () => {
    try {
      setLoadingTags(true);
      const response = await api.get<{ tags: string[]; count: number }>('/api/shopify/products/all-tags');
      
      if (response.data && response.data.tags) {
        setAllTags(response.data.tags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      showMessage('Failed to fetch tags', 'error');
    } finally {
      setLoadingTags(false);
    }
  }, []);

  // Auto suggest tags for single product
  const handleAutoSuggestTags = async (productId: number, productTitle: string): Promise<void> => {
    try {
      setAutoSuggestLoading(true);
      
      const product = products.find(p => p.id === productId);
      const currentTags = product?.tags || [];
      const createdDate = product?.created_at || product?.createdAt || null;
      
      const suggestions = tagEngine.suggestTags(productTitle, currentTags, createdDate);
      
      setShowSuggestions(prev => ({
        ...prev,
        [productId]: suggestions
      }));
      
      if (suggestions.length > 0) {
        const hasNewTag = suggestions.includes('New');
        const message = hasNewTag 
          ? `為 "${productTitle}" 生成了 ${suggestions.length} 個標籤建議（包含New標籤）`
          : `為 "${productTitle}" 生成了 ${suggestions.length} 個標籤建議`;
        showMessage(message, 'info');
      } else {
        showMessage('沒有找到適合的標籤建議', 'info');
      }
    } catch (error) {
      console.error('Error generating tag suggestions:', error);
      showMessage('生成標籤建議失敗', 'error');
    } finally {
      setAutoSuggestLoading(false);
    }
  };

  // Batch auto suggest for all products
  const handleBatchAutoSuggest = async (): Promise<void> => {
    try {
      setAutoSuggestLoading(true);
      
      const newSuggestions: {[key: number]: string[]} = {};
      let totalSuggestions = 0;
      let newTagCount = 0;
      
      filteredProducts.forEach(product => {
        const createdDate = product.created_at || product.createdAt || null;
        const suggestions = tagEngine.suggestTags(product.title, product.tags || [], createdDate);
        
        if (suggestions.length > 0) {
          newSuggestions[product.id] = suggestions;
          totalSuggestions += suggestions.length;
          
          if (suggestions.includes('New')) {
            newTagCount++;
          }
        }
      });
      
      setShowSuggestions(newSuggestions);
      
      if (totalSuggestions > 0) {
        const message = newTagCount > 0 
          ? `為 ${Object.keys(newSuggestions).length} 個產品生成了 ${totalSuggestions} 個標籤建議（${newTagCount} 個產品建議添加New標籤）`
          : `為 ${Object.keys(newSuggestions).length} 個產品生成了 ${totalSuggestions} 個標籤建議`;
        showMessage(message, 'success');
      } else {
        showMessage('沒有找到適合的標籤建議', 'info');
      }
    } catch (error) {
      console.error('Error generating batch suggestions:', error);
      showMessage('批量生成標籤建議失敗', 'error');
    } finally {
      setAutoSuggestLoading(false);
    }
  };

  // Apply suggested tag to product
  const handleApplySuggestedTag = async (productId: number, suggestedTag: string): Promise<void> => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;

      const updatedTags = [...product.tags, suggestedTag];
      
      const response = await api.put(`/api/shopify/products/${productId}/tags`, {
        tags: updatedTags
      });

      if (response.data.success) {
        setProducts(products => products.map(p => 
          p.id === productId 
            ? { ...p, tags: updatedTags }
            : p
        ));

        setShowSuggestions(prev => ({
          ...prev,
          [productId]: prev[productId]?.filter(tag => tag !== suggestedTag) || []
        }));

        if (!allTags.includes(suggestedTag)) {
          setAllTags(prev => [...prev, suggestedTag]);
        }

        showMessage(`標籤 "${suggestedTag}" 已添加`, 'success');
      }
    } catch (error) {
      console.error('Error applying suggested tag:', error);
      showMessage('添加標籤失敗', 'error');
    }
  };

  // Initial load
  useEffect(() => {
    fetchProducts();
    fetchAllTags();
  }, []);

  // Filter products locally based on search term
  useEffect(() => {
    let filtered = products;

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (tagFilter) {
      filtered = filtered.filter(product =>
        product.tags.some(tag => tag.toLowerCase().includes(tagFilter.toLowerCase()))
      );
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, tagFilter]);

  // Handle search
  const handleSearch = (): void => {
    setLastProductId(null);
    setHasMore(true);
    fetchProducts(null, false);
  };

  // Handle product selection
  const handleSelectProduct = (productId: number): void => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Handle select all
  const handleSelectAll = (): void => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleLoadMore = (): void => {
    if (lastProductId && hasMore) {
      fetchProducts(lastProductId, true);
    }
  };

  // Handle bulk tag addition
  const handleBulkAddTag = async (): Promise<void> => {
    if (!bulkTag.trim() || selectedProducts.size === 0) {
      showMessage('Please enter a tag and select products', 'warning');
      return;
    }

    try {
      setBulkOperationLoading(true);
      
      const response = await api.post('/api/shopify/products/bulk-update-tags', {
        product_ids: Array.from(selectedProducts),
        tags_to_add: [bulkTag.trim()],
        operation: 'add'
      });

      if (response.data.success) {
        showMessage(response.data.message, 'success');
        
        setProducts((products: Product[]) => products.map(product => {
          if (selectedProducts.has(product.id)) {
            const updatedTags = Array.from(new Set([...product.tags, bulkTag.trim()]));
            return { ...product, tags: updatedTags };
          }
          return product;
        }));

        setAllTags((prev: string[]) => [...Array.from(new Set([...prev, bulkTag.trim()]))]);
        
        setSelectedProducts(new Set());
        setBulkTag('');
      }
    } catch (error) {
      console.error('Error bulk adding tags:', error);
      showMessage((error as any).response?.data?.message || 'Failed to add tags', 'error');
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleReset = (): void => {
    setSearchTerm('');
    setTagFilter('');
    setLastProductId(null);
    setHasMore(true);
    fetchProducts(null, false);
  };

  // Handle single tag removal
  const handleRemoveTag = async (productId: number, tagToRemove: string): Promise<void> => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;

      const updatedTags = product.tags.filter(tag => tag !== tagToRemove);
      
      const response = await api.put(`/api/shopify/products/${productId}/tags`, {
        tags: updatedTags
      });

      if (response.data.success) {
        setProducts((products: Product[]) => products.map(p => 
          p.id === productId 
            ? { ...p, tags: updatedTags }
            : p
        ));
        
        showMessage(`Tag "${tagToRemove}" removed`, 'success');
      }
    } catch (error) {
      console.error('Error removing tag:', error);
      showMessage('Failed to remove tag', 'error');
    }
  };

  // Handle edit tags
  const handleEditTags = (product: Product): void => {
    setEditingProduct(product.id);
    setEditTags(product.tags.join(', '));
  };

  // Handle save edited tags
  const handleSaveEditTags = async (): Promise<void> => {
    if (!editingProduct) return;

    try {
      setEditLoading(true);
      
      const newTagsArray = editTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);

      const response = await api.put(`/api/shopify/products/${editingProduct}/tags`, {
        tags: newTagsArray
      });

      if (response.data.success) {
        setProducts((products: Product[]) => products.map(product => 
          product.id === editingProduct 
            ? { ...product, tags: newTagsArray }
            : product
        ));

        newTagsArray.forEach(tag => {
          if (!allTags.includes(tag)) {
            setAllTags((prev: string[]) => [...prev, tag]);
          }
        });

        setEditingProduct(null);
        setEditTags('');
        showMessage('Tags updated successfully', 'success');
      }
    } catch (error) {
      console.error('Error updating tags:', error);
      showMessage('Failed to update tags', 'error');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = (): void => {
    setEditingProduct(null);
    setEditTags('');
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TagIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Product Tag Management
          </Typography>
        </Box>

        {/* Search and Filter Controls */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search Products"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by product name or SKU..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={allTags}
              value={tagFilter}
              onChange={(event, newValue) => setTagFilter(newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Tag"
                  placeholder="Select or type a tag..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              freeSolo
              loading={loadingTags}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                disabled={loading}
              >
                Search
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                disabled={loading}
              >
                Reset
              </Button>

              <Tooltip title="為所有顯示的產品自動建議標籤">
                <Button
                  variant="outlined"
                  onClick={handleBatchAutoSuggest}
                  startIcon={autoSuggestLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                  disabled={autoSuggestLoading || filteredProducts.length === 0}
                  sx={{ 
                    bgcolor: 'secondary.50',
                    borderColor: 'secondary.main',
                    color: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'secondary.100',
                      borderColor: 'secondary.dark'
                    }
                  }}
                >
                  智能標籤
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        {/* Bulk Operations */}
        {selectedProducts.size > 0 && (
          <Card sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.800' }}>
                Bulk Operations ({selectedProducts.size} products selected)
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={allTags}
                    value={bulkTag}
                    onChange={(event, newValue) => setBulkTag(newValue || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tag to Add"
                        placeholder="Enter or select a tag..."
                        variant="outlined"
                        size="small"
                      />
                    )}
                    freeSolo
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    onClick={handleBulkAddTag}
                    startIcon={bulkOperationLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                    disabled={!bulkTag.trim() || bulkOperationLoading}
                  >
                    Add Tag to Selected
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Paper>

      {/* Products Table */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Products ({filteredProducts.length})
          </Typography>
          
          <Button
            variant="outlined"
            onClick={handleSelectAll}
            startIcon={<SelectAllIcon />}
            disabled={filteredProducts.length === 0}
          >
            {selectedProducts.size === filteredProducts.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredProducts.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No products found. Try adjusting your search criteria.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0}
                      indeterminate={selectedProducts.size > 0 && selectedProducts.size < filteredProducts.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Current Tags</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow 
                    key={product.id}
                    sx={{ 
                      bgcolor: selectedProducts.has(product.id) ? 'action.selected' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProducts.has(product.id)}
                        onChange={() => handleSelectProduct(product.id)}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.title}
                            style={{
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              marginRight: 12,
                              borderRadius: 4,
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        )}
                        <Box>
                          <Typography variant="subtitle2" noWrap sx={{ maxWidth: 200 }}>
                            {product.title}
                          </Typography>
                          {(product.variants_count && product.variants_count > 1) && (
                            <Typography variant="caption" color="textSecondary">
                              {product.variants_count} variants
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">{product.sku || 'N/A'}</Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        ${typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price || '0').toFixed(2)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {product.inventory || 0}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      {editingProduct === product.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            size="small"
                            value={editTags}
                            onChange={(e) => setEditTags(e.target.value)}
                            placeholder="Enter tags separated by commas..."
                            sx={{ minWidth: 200 }}
                          />
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSaveEditTags}
                            disabled={editLoading}
                          >
                            {editLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ maxWidth: 350 }}>
                          {/* Current Tags */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {product.tags.length === 0 ? (
                              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                No tags
                              </Typography>
                            ) : (
                              product.tags.map((tag, index) => (
                                <Chip
                                  key={index}
                                  label={tag}
                                  size="small"
                                  onDelete={() => handleRemoveTag(product.id, tag)}
                                  color="primary"
                                  variant="outlined"
                                />
                              ))
                            )}
                          </Box>
                          
                          {/* Suggested Tags */}
                          {showSuggestions[product.id] && showSuggestions[product.id].length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                                建議標籤:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {showSuggestions[product.id].map((suggestedTag, index) => (
                                  <Chip
                                    key={index}
                                    label={suggestedTag}
                                    size="small"
                                    color={suggestedTag === 'New' ? 'error' : 'secondary'}
                                    variant="outlined"
                                    onClick={() => handleApplySuggestedTag(product.id, suggestedTag)}
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { 
                                        bgcolor: suggestedTag === 'New' ? 'error.50' : 'secondary.50',
                                        borderColor: suggestedTag === 'New' ? 'error.main' : 'secondary.main'
                                      }
                                    }}
                                    icon={<AddIcon sx={{ fontSize: 16 }} />}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditTags(product)}
                          disabled={editingProduct === product.id}
                          title="Edit tags"
                        >
                          <EditIcon />
                        </IconButton>
                        
                        <Tooltip title="自動建議標籤">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleAutoSuggestTags(product.id, product.title)}
                            disabled={autoSuggestLoading}
                          >
                            <PsychologyIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Load More */}
        {hasMore && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={!lastProductId}
            >
              Load More Products
            </Button>
          </Box>
        )}
      </Paper>

      {/* Statistics */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Products
                </Typography>
                <Typography variant="h4">
                  {products.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Selected Products
                </Typography>
                <Typography variant="h4">
                  {selectedProducts.size}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Unique Tags
                </Typography>
                <Typography variant="h4">
                  {allTags.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Products with Suggestions
                </Typography>
                <Typography variant="h4">
                  {Object.keys(showSuggestions).filter(id => showSuggestions[parseInt(id)].length > 0).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={messageType}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductTagManager;